import React, { useState, useEffect } from 'react';
import { Container, Table, Badge, Button, Modal } from 'react-bootstrap';
import { Download, FileText, Image, File } from 'react-bootstrap-icons';
import applicationService from '../../services/applicationService';
import config from '../../config';

const ApplicationsManagePage = () => {
  const [applications, setApplications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      const data = await applicationService.getApplications();
      setApplications(data);
    } catch (error) {
      console.error('Failed to load applications:', error);
    }
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setShowModal(true);
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await applicationService.updateApplicationStatus(id, newStatus);
      loadApplications(); // Reload the list after update
      setShowModal(false);
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      accepted: 'success',
      rejected: 'danger',
      interviewing: 'info'
    };
    return <Badge bg={variants[status]}>{status}</Badge>;
  };

  const getFileIcon = (filePath) => {
    if (!filePath) return null;
    
    const extension = filePath.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png'].includes(extension)) {
      return <Image className="text-primary" size={16} />;
    } else if (extension === 'pdf') {
      return <FileText className="text-danger" size={16} />;
    } else {
      return <File className="text-secondary" size={16} />;
    }
  };

  const getFileName = (filePath) => {
    if (!filePath) return null;
    return filePath.split('/').pop();
  };

  const getFileType = (filePath) => {
    if (!filePath) return null;
    
    const extension = filePath.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg'].includes(extension)) {
      return 'JPG';
    } else if (extension === 'png') {
      return 'PNG';
    } else if (extension === 'pdf') {
      return 'PDF';
    } else {
      return extension?.toUpperCase() || 'DOSYA';
    }
  };

  const handleDownloadFile = (filePath) => {
    if (!filePath) return;
    
    const fullUrl = `${config.API_URL}${filePath}`;
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = getFileName(filePath);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePreviewFile = (filePath) => {
    if (!filePath) return;
    
    const fullUrl = `${config.API_URL}${filePath}`;
    window.open(fullUrl, '_blank');
  };

  return (
    <Container fluid>
      <h2 className="mb-4">Job Applications</h2>
      
      <Table responsive striped hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Position</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Applied Date</th>
            <th>Status</th>
            <th>File</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {applications.map(application => (
            <tr key={application.id}>
              <td>{application.full_name}</td>
              <td>{application.position}</td>
              <td>{application.email}</td>
              <td>{application.phone}</td>
              <td>{application.appliedAt}</td>
              <td>{getStatusBadge(application.status)}</td>
              <td>
                {application.file_path ? (
                  <div className="d-flex align-items-center gap-2">
                    {getFileIcon(application.file_path)}
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => handlePreviewFile(application.file_path)}
                      className="text-decoration-none"
                    >
                      {getFileType(application.file_path)} Gör
                    </Button>
                  </div>
                ) : (
                  <span className="text-muted small">No file</span>
                )}
              </td>
              <td>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => handleViewDetails(application)}
                >
                  View Details
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Application Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApplication && (
            <div>
              <h4>{selectedApplication.full_name}</h4>
              <p><strong>Position:</strong> {selectedApplication.position}</p>
              <p><strong>Email:</strong> {selectedApplication.email}</p>
              <p><strong>Phone:</strong> {selectedApplication.phone}</p>
              <p><strong>Applied Date:</strong> {selectedApplication.appliedAt}</p>
              <p><strong>Current Status:</strong> {getStatusBadge(selectedApplication.status)}</p>
              
              {selectedApplication.notes && (
                <div className="mt-3">
                  <h6>Notes:</h6>
                  <p className="text-muted">{selectedApplication.notes}</p>
                </div>
              )}
              
              {selectedApplication.file_path && (
                <div className="mt-3">
                  <h6>Attached File:</h6>
                  <div className="d-flex align-items-center gap-2 p-3 bg-light rounded">
                    {getFileIcon(selectedApplication.file_path)}
                    <span className="flex-grow-1 small text-muted">
                      {getFileName(selectedApplication.file_path)}
                    </span>
                    <div className="d-flex gap-2">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => handlePreviewFile(selectedApplication.file_path)}
                      >
                        {getFileType(selectedApplication.file_path)} Gör
                      </Button>
                      <Button 
                        variant="outline-success" 
                        size="sm"
                        onClick={() => handleDownloadFile(selectedApplication.file_path)}
                      >
                        <Download className="me-1" size={14} />
                        İndir
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-4">
                <h5>Update Status</h5>
                <div className="d-flex gap-2">
                  <Button 
                    variant="warning" 
                    onClick={() => handleStatusUpdate(selectedApplication.id, 'interviewing')}
                  >
                    Mark as Interviewing
                  </Button>
                  <Button 
                    variant="success" 
                    onClick={() => handleStatusUpdate(selectedApplication.id, 'accepted')}
                  >
                    Accept
                  </Button>
                  <Button 
                    variant="danger" 
                    onClick={() => handleStatusUpdate(selectedApplication.id, 'rejected')}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ApplicationsManagePage;